import React, { useEffect, useMemo, useState } from "react";
import { confirmDialog } from "../../utils/toast";

const PAGE_SIZE = 9;

function maskPhone(phoneMid, phoneLast) {
  const mid = String(phoneMid || "").trim();
  const last = String(phoneLast || "").trim();
  if (last.length === 4) return `010-****-${last}`;
  if (mid.length === 4 || last.length > 0) return `010-****-${last || "****"}`;
  return "-";
}

export default function AdminApprovalPage({ me }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [statusFilter, setStatusFilter] = useState("PENDING"); // PENDING | APPROVED | ALL
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [approveRole, setApproveRole] = useState("WORKER"); // 승인 시 부여할 등급
  // 계단식: 승인자 등급별로 '부여 가능한 하위 등급'만 노출.
  const actorRole = String(me?.roleCode || "").toUpperCase();
  const ROLE_OPTIONS = [
    { code: "WORKER", label: "설치팀장 (현장)" },
    { code: "BRANCH_MANAGER", label: "안전관리자 (지점)" },
    { code: "HQ_MANAGER", label: "관제담당자 (본사)" },
  ];
  /*
    부여할 수 있는 등급 — 자기보다 «낮은» 등급만.
    고객사 관리자(ADMIN)는 «본사 관제담당자»를 만들 수 없다. 본사 등급은 운영사(S/A)만 준다.
  */
  const assignableRoles =
    actorRole === "SUPER_ADMIN"
      ? ["HQ_MANAGER", "BRANCH_MANAGER", "WORKER"]
      : actorRole === "ADMIN"
      ? ["WORKER"] // 고객사 관리자는 «설치팀장»만 승인한다.
      : actorRole === "HQ_MANAGER"
      ? ["BRANCH_MANAGER", "WORKER"]
      : actorRole === "BRANCH_MANAGER"
      ? ["WORKER"]
      : [];
  const roleOptions = ROLE_OPTIONS.filter((o) => assignableRoles.includes(o.code));

  const loadUsers = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setMsg(data.error || data.message || "사용자 목록 조회 실패");
        setAllUsers([]);
        return;
      }
      const map = new Map();
      (data.users || []).forEach((row) => {
        if (!map.has(row.UserIdx)) {
          map.set(row.UserIdx, {
            userIdx: row.UserIdx,
            userId: row.UserID,
            userName: row.UserName,
            userPathKey: row.UserPathKey,
            companyName: row.CompanyName || "",
            phoneMid: row.PhoneMid || "",
            phoneLast: row.PhoneLast || "",
            roleCode: row.RoleCode,
            approvalStatus: Number(row.ApprovalStatus || 0),
          });
        }
      });
      setAllUsers(Array.from(map.values()));
    } catch (err) {
      setMsg("사용자 목록을 불러오는 중 오류가 발생했습니다.");
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const counts = useMemo(() => {
    const pending = allUsers.filter((u) => u.approvalStatus !== 1).length;
    const approved = allUsers.filter((u) => u.approvalStatus === 1).length;
    return { pending, approved, all: allUsers.length };
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return allUsers.filter((u) => {
      if (statusFilter === "PENDING" && u.approvalStatus === 1) return false;
      if (statusFilter === "APPROVED" && u.approvalStatus !== 1) return false;
      if (!q) return true;
      const hay = [u.userId, u.userName, u.companyName, u.userPathKey, u.roleCode]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allUsers, statusFilter, search]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), pageCount);
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, page, pageCount]);

  const selectedUser = useMemo(
    () => allUsers.find((u) => u.userIdx === selectedIdx) || null,
    [allUsers, selectedIdx]
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    if (filteredUsers.length === 0) {
      if (selectedIdx !== null) setSelectedIdx(null);
      return;
    }
    const visible = filteredUsers.some((u) => u.userIdx === selectedIdx);
    if (!visible) setSelectedIdx(filteredUsers[0].userIdx);
  }, [filteredUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async (userIdx, roleCode) => {
    try {
      const res = await fetch("/api/admin/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx, roleCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "승인 처리 실패");
        return;
      }
      alert("승인 완료");
      loadUsers();
    } catch (err) {
      alert("승인 처리 중 오류가 발생했습니다.");
    }
  };

  const handleBlock = async (userIdx) => {
    if (!(await confirmDialog("이 사용자를 차단하시겠습니까?", { title: "사용자 차단", danger: true, confirmText: "차단" }))) return;
    try {
      const res = await fetch("/api/admin/block", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "차단 처리 실패");
        return;
      }
      alert("차단 완료");
      loadUsers();
    } catch (err) {
      alert("차단 처리 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async (userIdx, userId) => {
    if (
      !(await confirmDialog(
        `[${userId}] 사용자를 삭제하시겠습니까?\n삭제된 사용자는 복구되지 않습니다.`,
        { title: "사용자 삭제", danger: true, confirmText: "삭제" }
      ))
    )
      return;
    try {
      const res = await fetch("/api/admin/user/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "사용자 삭제 실패");
        return;
      }
      alert("사용자 삭제 완료");
      loadUsers();
    } catch (err) {
      alert("사용자 삭제 중 오류가 발생했습니다.");
    }
  };

  const FilterTab = ({ value, label, count }) => (
    <button
      type="button"
      onClick={() => setStatusFilter(value)}
      className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
        statusFilter === value
          ? "bg-blue-600 text-white"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
      }`}
    >
      {label} {count}
    </button>
  );

  return (
    <div className="h-full min-h-0 overflow-y-auto pr-1">
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">승인 관리</h2>

        {msg && (
          <div className="rounded-xl bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {msg}
          </div>
        )}

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <FilterTab value="PENDING" label="승인 대기" count={counts.pending} />
              <FilterTab value="APPROVED" label="승인 완료" count={counts.approved} />
              <FilterTab value="ALL" label="전체" count={counts.all} />
            </div>
            <button
              type="button"
              onClick={loadUsers}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              새로고침
            </button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-300">
              불러오는 중...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:min-h-[460px] lg:grid-cols-12">
              {/* 좌측 목록 */}
              <div className="lg:col-span-4">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="아이디 / 이름 / 회사 검색"
                  className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />

                <div className="space-y-1.5">
                  {filteredUsers.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-300">
                      {search ? "검색 결과가 없습니다." : "사용자가 없습니다."}
                    </div>
                  ) : (
                    pagedUsers.map((u) => {
                      const active = u.userIdx === selectedIdx;
                      const isApproved = u.approvalStatus === 1;
                      return (
                        <button
                          key={u.userIdx}
                          type="button"
                          onClick={() => setSelectedIdx(u.userIdx)}
                          className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                            active
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                              : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="line-clamp-1 text-sm font-semibold text-gray-900 dark:text-white">
                              {u.userName || "-"}
                            </div>
                            <div className="line-clamp-1 text-xs text-gray-500 dark:text-gray-400">
                              {u.userId}
                              {u.companyName ? ` · ${u.companyName}` : ""}
                            </div>
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              isApproved
                                ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300"
                                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300"
                            }`}
                          >
                            {isApproved ? "승인됨" : "대기"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>

                {filteredUsers.length > PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      ◀ 이전
                    </button>
                    <div className="min-w-[48px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {page} / {pageCount}
                    </div>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      disabled={page >= pageCount}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      다음 ▶
                    </button>
                  </div>
                )}
              </div>

              {/* 우측 상세 */}
              <div className="lg:col-span-8">
                {!selectedUser ? (
                  <div className="flex h-full min-h-[260px] items-center justify-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400 dark:border-gray-600">
                    왼쪽에서 사용자를 선택하세요.
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700 lg:h-full lg:min-h-[420px]">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {selectedUser.userName || "-"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {selectedUser.userId}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
                          selectedUser.approvalStatus === 1
                            ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300"
                            : "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300"
                        }`}
                      >
                        {selectedUser.approvalStatus === 1 ? "승인됨" : "승인 대기"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <DetailField label="회사명" value={selectedUser.companyName || "-"} />
                      <DetailField
                        label="전화번호"
                        value={maskPhone(selectedUser.phoneMid, selectedUser.phoneLast)}
                      />
                      <DetailField label="권한" value={selectedUser.roleCode || "-"} />
                      <DetailField label="PathKey" value={selectedUser.userPathKey || "-"} />
                      <DetailField label="UserIdx" value={String(selectedUser.userIdx)} />
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                      {selectedUser.approvalStatus !== 1 && (
                        <div className="flex items-center gap-2">
                          <select
                            value={approveRole}
                            onChange={(e) => setApproveRole(e.target.value)}
                            className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                            title="부여할 등급 선택"
                          >
                            {roleOptions.map((o) => (
                              <option key={o.code} value={o.code}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => handleApprove(selectedUser.userIdx, approveRole)}
                            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                          >
                            등급 지정 · 승인
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleBlock(selectedUser.userIdx)}
                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                      >
                        차단
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(selectedUser.userIdx, selectedUser.userId)}
                        className="rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-[#16202C]">
      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 break-all text-sm text-gray-800 dark:text-gray-100">{value}</div>
    </div>
  );
}
